<template>
  <div
    class="border-y sm:border border-gray-200 dark:border-gray-600 sm:rounded-lg overflow-hidden -mx-2 sm:mx-0"
  >
    <!-- Preference Header -->
    <div class="p-2 sm:p-3 bg-gray-50 dark:bg-gray-700">
      <!-- Edit mode -->
      <PreferenceReviewCardEdit
        v-if="isEditingPref"
        :preference="preference"
        :prospect="prospect"
        @saved="handleEditSaved"
        @cancel="cancelEditing"
      />

      <!-- View mode -->
      <PreferenceReviewCardView
        v-else
        :preference="preference"
        :prospect="prospect"
        :selection="selection"
        :selection-name="getEmployeeName(selection?.employeeId)"
        :preferred-team-names="preferredTeamNames"
        :is-expanded="isExpanded"
        @toggle-expanded="$emit('toggle-expanded')"
        @start-editing="startEditing"
        @confirm-delete="confirmDelete"
      />
    </div>

    <!-- Preference Content (expandable) -->
    <div
      v-show="isExpanded"
      class="p-2 sm:p-4 border-t border-gray-200 dark:border-gray-600 dark:bg-gray-800"
    >
      <!-- Preferred Teams -->
      <div
        v-if="preferredTeamNames.length > 0"
        class="mb-3 p-2 bg-blue-50 dark:bg-blue-900/30 rounded flex items-center gap-2"
      >
        <UsersIcon class="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <span class="text-sm text-blue-800 dark:text-blue-200">
          <span class="font-medium">Voorkeur teams:</span>
          {{ preferredTeamNames.join(', ') }}
        </span>
      </div>

      <!-- Notes if present -->
      <p
        v-if="preference.notes"
        class="text-sm text-gray-600 dark:text-gray-300 italic mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/30 rounded"
      >
        {{ preference.notes }}
      </p>

      <!-- Employee Selection Radio Buttons -->
      <div class="space-y-3">
        <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
          >Selecteer medewerker:</label
        >

        <!-- Radio options grid -->
        <div class="space-y-2">
          <!-- Preferred employees as radio options -->
          <label
            v-for="empId in preference.preferred_employees"
            :key="empId"
            class="flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer"
            :class="
              selection?.employeeId === empId && !selection?.isCustom
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            "
          >
            <input
              type="radio"
              :name="`pref-${preference.id}`"
              :value="empId"
              :checked="selection?.employeeId === empId && !selection?.isCustom"
              @change="selectEmployee(empId)"
              class="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
            />
            <div class="flex-1 flex items-center justify-between">
              <div>
                <span class="font-medium text-gray-900 dark:text-white">{{
                  getEmployeeName(empId)
                }}</span>
                <span
                  v-if="employeeDetails[empId]"
                  class="ml-2 text-xs text-gray-500 dark:text-gray-400"
                >
                  {{ getEmployeeInfo(empId) }}
                </span>
              </div>
              <span
                v-if="preferenceAvailability[empId]"
                class="flex items-center gap-1 flex-wrap justify-end"
              >
                <span
                  class="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
                  :class="[
                    getMatchClass(preferenceAvailability[empId].matchPercentVoorgesteld),
                    hasProposals(empId) ? 'cursor-help' : ''
                  ]"
                  @mouseenter="showProposalsTooltip($event, empId)"
                  @mouseleave="hideProposalsTooltip"
                >
                  {{ preferenceAvailability[empId].matchPercentVoorgesteld?.toFixed(0) || 0 }}% obv
                  voorgesteld
                </span>
                <span
                  class="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
                  :class="getMatchClass(preferenceAvailability[empId].matchPercentPresourcing)"
                >
                  {{ preferenceAvailability[empId].matchPercentPresourcing?.toFixed(0) || 0 }}% obv
                  toegewezen
                </span>
              </span>
            </div>
          </label>

          <!-- Comparison employees (added via "Andere medewerker kiezen") -->
          <label
            v-for="compEmp in comparisonEmployees"
            :key="compEmp.employeeId"
            class="flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer"
            :class="
              selection?.employeeId === compEmp.employeeId && selection?.isCustom
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            "
          >
            <input
              type="radio"
              :name="`pref-${preference.id}`"
              :value="compEmp.employeeId"
              :checked="selection?.employeeId === compEmp.employeeId && selection?.isCustom"
              @change="selectComparisonEmployee(compEmp)"
              class="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
            />
            <div class="flex-1 flex items-center justify-between">
              <div>
                <span class="font-medium text-gray-900 dark:text-white">{{ compEmp.name }}</span>
                <span class="ml-2 text-xs text-gray-500 dark:text-gray-400">(toegevoegd)</span>
                <span
                  v-if="employeeDetails[compEmp.employeeId]"
                  class="ml-2 text-xs text-gray-500 dark:text-gray-400"
                >
                  {{ getEmployeeInfo(compEmp.employeeId) }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <span
                  v-if="preferenceAvailability[compEmp.employeeId]"
                  class="flex items-center gap-1 flex-wrap justify-end"
                >
                  <span
                    class="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
                    :class="[
                      getMatchClass(
                        preferenceAvailability[compEmp.employeeId].matchPercentVoorgesteld
                      ),
                      hasProposals(compEmp.employeeId) ? 'cursor-help' : ''
                    ]"
                    @mouseenter="showProposalsTooltip($event, compEmp.employeeId)"
                    @mouseleave="hideProposalsTooltip"
                  >
                    {{
                      preferenceAvailability[compEmp.employeeId].matchPercentVoorgesteld?.toFixed(
                        0
                      ) || 0
                    }}% obv voorgesteld
                  </span>
                  <span
                    class="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
                    :class="
                      getMatchClass(
                        preferenceAvailability[compEmp.employeeId].matchPercentPresourcing
                      )
                    "
                  >
                    {{
                      preferenceAvailability[compEmp.employeeId].matchPercentPresourcing?.toFixed(
                        0
                      ) || 0
                    }}% obv toegewezen
                  </span>
                </span>
                <button
                  @click.stop.prevent="$emit('remove-comparison', compEmp.employeeId)"
                  class="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
                  title="Verwijderen uit vergelijking"
                >
                  <XMarkIcon class="h-4 w-4" />
                </button>
              </div>
            </div>
          </label>
        </div>

        <!-- "Add employee for comparison" button -->
        <button
          @click="$emit('add-employee')"
          type="button"
          class="w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:border-blue-500 dark:hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
        >
          <PlusIcon class="h-4 w-4" />
          Andere medewerker toevoegen
        </button>
      </div>

      <!-- Beschikbaarheid Section -->
      <div
        v-if="preference.preferred_employees?.length > 0 || (comparisonEmployees?.length || 0) > 0"
        class="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4"
      >
        <button
          type="button"
          @click="$emit('toggle-availability')"
          class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ChevronDownIcon v-if="isAvailabilityExpanded" class="h-4 w-4" />
          <ChevronRightIcon v-else class="h-4 w-4" />
          <ChartBarIcon class="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          Beschikbaarheid
        </button>

        <div v-if="isAvailabilityExpanded" class="space-y-4 mt-4">
          <!-- Chart for each preferred employee -->
          <EmployeeAvailabilityCard
            v-for="empId in preference.preferred_employees"
            :key="empId"
            :employee-id="empId"
            :employee-name="getEmployeeName(empId)"
            :employee="getEmployeeWithWeeks(empId)"
            :employee-info="getEmployeeInfo(empId)"
            :period-start="periodStart"
            :period-end="periodEnd"
            :potential-dpw="Number(preference.dpw) || 0"
            :potential-label="preference.role ? `${preference.role} (nieuw)` : 'Dit project'"
            :is-selected="selection?.employeeId === empId"
            :is-expanded="isEmployeeGraphExpanded(empId)"
            :badge="selection?.employeeId === empId ? 'selected' : null"
            @toggle="$emit('toggle-employee-graph', empId)"
          />

          <!-- Charts for comparison employees -->
          <EmployeeAvailabilityCard
            v-for="compEmp in comparisonEmployees"
            :key="'comp-' + compEmp.employeeId"
            :employee-id="compEmp.employeeId"
            :employee-name="compEmp.name"
            :employee="getEmployeeWithWeeks(compEmp.employeeId)"
            :employee-info="getEmployeeInfo(compEmp.employeeId)"
            :period-start="periodStart"
            :period-end="periodEnd"
            :potential-dpw="Number(compEmp.dpw || preference.dpw) || 0"
            :potential-label="preference.role ? `${preference.role} (nieuw)` : 'Dit project'"
            :is-selected="selection?.employeeId === compEmp.employeeId && selection?.isCustom"
            :is-expanded="isEmployeeGraphExpanded('comp-' + compEmp.employeeId)"
            :badge="
              selection?.employeeId === compEmp.employeeId && selection?.isCustom
                ? 'selected'
                : 'added'
            "
            :show-remove-button="
              !(selection?.employeeId === compEmp.employeeId && selection?.isCustom)
            "
            @toggle="$emit('toggle-employee-graph', 'comp-' + compEmp.employeeId)"
            @remove="$emit('remove-comparison', compEmp.employeeId)"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Delete confirmation modal -->
  <ConfirmActionModal
    :open="showDeleteConfirm"
    title="Rol verwijderen"
    :message="`Weet je zeker dat je &quot;${preference.role || 'deze rol'}&quot; wilt verwijderen?`"
    confirm-label="Verwijderen"
    variant="danger"
    @confirm="handleConfirmDelete"
    @cancel="showDeleteConfirm = false"
  />

  <!-- Tooltip rendered into body so it isn't clipped by overflow-hidden ancestors -->
  <Teleport to="body">
    <div
      v-if="tooltipVisible"
      class="fixed z-[9999] -translate-x-1/2 -translate-y-full pointer-events-none"
      :style="{ top: tooltipPos.top + 'px', left: tooltipPos.left + 'px' }"
    >
      <div class="bg-gray-900 text-white text-xs rounded-lg p-3 whitespace-nowrap shadow-lg">
        <div class="font-semibold mb-1">Voorgesteld voor:</div>
        <ul class="space-y-1">
          <li v-for="(entry, idx) in tooltipEntries" :key="idx">
            {{ entry.organizationName }}
            <span v-if="entry.role"> - {{ entry.role }}</span>
            <span v-if="entry.dpw"> ({{ entry.dpw }}d/w)</span>
          </li>
        </ul>
      </div>
      <div
        class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"
      ></div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  ChartBarIcon,
  UsersIcon
} from '@heroicons/vue/24/outline'
import ConfirmActionModal from '../modals/ConfirmActionModal.vue'
import EmployeeAvailabilityCard from './EmployeeAvailabilityCard.vue'
import PreferenceReviewCardEdit from './PreferenceReviewCardEdit.vue'
import PreferenceReviewCardView from './PreferenceReviewCardView.vue'
import { hoursService } from '../../../services/hoursService.js'
import {
  localProposalHoursForWeek,
  indexLocalProposalsByEmployee
} from '../../../utils/proposalAvailability.js'

const props = defineProps({
  preference: {
    type: Object,
    required: true
  },
  prospect: {
    type: Object,
    default: null
  },
  selection: {
    type: Object,
    default: null
  },
  comparisonEmployees: {
    type: Array,
    default: () => []
  },
  employeeMap: {
    type: Object,
    default: () => ({})
  },
  employeeDetails: {
    type: Object,
    default: () => ({})
  },
  employeeAvailability: {
    type: Object,
    default: () => ({})
  },
  teams: {
    type: Array,
    default: () => []
  },
  isExpanded: {
    type: Boolean,
    default: true
  },
  isAvailabilityExpanded: {
    type: Boolean,
    default: true
  },
  expandedEmployeeGraphs: {
    type: Object,
    default: () => ({})
  },
  startInEditMode: {
    type: Boolean,
    default: false
  },
  // All locally-staged proposals across roles in this prospect.
  // Used to simulate matchPercentVoorgesteld so an employee proposed for *another*
  // role on the same prospect counts here, while their proposal for the *current*
  // role is excluded (no double-counting against themselves).
  localProposals: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'select',
  'select-comparison',
  'add-employee',
  'remove-comparison',
  'toggle-expanded',
  'toggle-availability',
  'toggle-employee-graph',
  'preference-updated',
  'delete-preference'
])

// ==================== Inline Editing ====================
const isEditingPref = ref(false)

function startEditing() {
  isEditingPref.value = true
}

// Start editing when startInEditMode prop is true (on mount or change)
watch(
  () => props.startInEditMode,
  val => {
    if (val && !isEditingPref.value) {
      startEditing()
    }
  },
  { immediate: true }
)

function cancelEditing() {
  isEditingPref.value = false
}

function handleEditSaved(data) {
  isEditingPref.value = false
  emit('preference-updated', data)
}

const showDeleteConfirm = ref(false)

function confirmDelete() {
  showDeleteConfirm.value = true
}

function handleConfirmDelete() {
  showDeleteConfirm.value = false
  emit('delete-preference', props.preference.id)
}

// Period dates
const periodStart = computed(() => {
  return (props.preference.start_date || props.prospect?.start_date)?.split('T')[0]
})

const periodEnd = computed(() => {
  return (props.preference.end_date || props.prospect?.expected_closing_date)?.split('T')[0]
})

const isLoadingAvailability = ref(false)

// Cache the raw presourcing response so re-running the local proposal simulation
// (when localProposals changes) doesn't require a new backend round-trip.
const rawPresourcingData = ref([])

const fetchPreferenceAvailability = async () => {
  const startDate = periodStart.value
  const endDate = periodEnd.value
  if (!startDate || !endDate) {
    rawPresourcingData.value = []
    return
  }

  isLoadingAvailability.value = true
  try {
    const result = await hoursService.getWeeklyAvailability({
      start_date: startDate,
      end_date: endDate
    })
    rawPresourcingData.value = result?.success && result.data ? result.data : []
  } catch (error) {
    console.error('Error fetching preference availability:', error)
    rawPresourcingData.value = []
  } finally {
    isLoadingAvailability.value = false
  }
}

const calculateMatchPercent = (employee, requiredDpw) => {
  if (!employee?.weeks?.length || !requiredDpw) return 0
  const matchingWeeks = employee.weeks.filter(
    week => Number(week.available_days || 0) >= requiredDpw
  ).length
  return (matchingWeeks / employee.weeks.length) * 100
}

// Recompute matchPercentVoorgesteld whenever the raw data, the preference, or
// the set of locally-staged proposals changes.
const preferenceAvailability = computed(() => {
  const data = rawPresourcingData.value
  if (!data?.length) return {}

  const dpw = Number(props.preference.dpw) || 0
  const currentPrefId = props.preference.id

  const localByEmployee = indexLocalProposalsByEmployee(props.localProposals, currentPrefId)

  const availMap = {}
  for (const emp of data) {
    const weeks = emp.weeks || []
    const matchPercentPresourcing = calculateMatchPercent(emp, dpw)

    let weeksOkVoorgesteld = 0
    if (dpw > 0 && weeks.length > 0) {
      const empLocal = localByEmployee.get(emp.simplicate_id) || []
      for (const week of weeks) {
        // Server-side proposed hours for this week, minus the hours that come
        // from the current preference (so an employee already proposed for THIS
        // role isn't subtracted from their own availability).
        const proposedHoursTotal = Number(week.proposed_hours || 0)
        const excludedSelfHours = (week.proposed_assignments || [])
          .filter(p => p.preference_id === currentPrefId)
          .reduce((sum, p) => sum + Number(p.hours_this_week || 0), 0)
        // Add hours for this employee's local proposals on OTHER roles in this prospect.
        const localOtherHours = empLocal.reduce(
          (sum, lp) => sum + localProposalHoursForWeek(lp, week.week_start, week.week_end),
          0
        )
        const proposedDeductionHours = Math.max(
          0,
          proposedHoursTotal - excludedSelfHours + localOtherHours
        )
        const adjustedAvailable = Number(week.available_days || 0) - proposedDeductionHours / 8
        if (adjustedAvailable >= dpw) weeksOkVoorgesteld++
      }
    }
    const matchPercentVoorgesteld = weeks.length ? (weeksOkVoorgesteld / weeks.length) * 100 : 0

    availMap[emp.simplicate_id] = {
      employee: emp,
      matchPercentPresourcing,
      matchPercentVoorgesteld
    }
  }
  return availMap
})

watch([periodStart, periodEnd], fetchPreferenceAvailability, {
  immediate: true
})

// Per-employee list of "where they're proposed" — feeds the tooltip on the
// voorgesteld badge. Aggregates server-side proposals (other prospects/roles
// already saved) and local proposals (other roles being staged in this prospect),
// excluding the current preference to avoid self-listing.
const proposalsByEmployee = computed(() => {
  const map = new Map()
  const data = rawPresourcingData.value
  const currentPrefId = props.preference.id
  const localProposalsList = props.localProposals || []
  const fallbackLocalLabel =
    props.prospect?.organization_name || props.prospect?.subject || 'Deze klus'

  for (const emp of data || []) {
    const entries = []
    const seen = new Set()

    // Server-side proposals — dedupe by prospect+preference since each week
    // contributes the same allocation.
    for (const week of emp.weeks || []) {
      for (const pa of week.proposed_assignments || []) {
        if (pa.preference_id === currentPrefId) continue
        const key = `srv-${pa.prospect_id}-${pa.preference_id ?? 'na'}`
        if (seen.has(key)) continue
        seen.add(key)
        entries.push({
          organizationName: pa.organization_name || pa.prospect_subject || 'Onbekende klant',
          role: pa.role || null,
          dpw: Number(pa.dpw) || 0
        })
      }
    }

    // Local proposals on OTHER roles in this prospect.
    for (const lp of localProposalsList) {
      if (lp.employeeId !== emp.simplicate_id) continue
      if (lp.preferenceId === currentPrefId) continue
      const key = `loc-${lp.preferenceId}`
      if (seen.has(key)) continue
      seen.add(key)
      entries.push({
        organizationName: fallbackLocalLabel,
        role: lp.role || null,
        dpw: Number(lp.dpw) || 0
      })
    }

    if (entries.length > 0) map.set(emp.simplicate_id, entries)
  }
  return map
})

// Tooltip state — single shared tooltip rendered via Teleport so it isn't
// clipped by the card's overflow-hidden wrapper.
const tooltipVisible = ref(false)
const tooltipPos = ref({ top: 0, left: 0 })
const tooltipEntries = ref([])

function hasProposals(empId) {
  const entries = proposalsByEmployee.value.get(empId)
  return !!entries && entries.length > 0
}

function showProposalsTooltip(event, empId) {
  const entries = proposalsByEmployee.value.get(empId)
  if (!entries || entries.length === 0) return
  const rect = event.currentTarget?.getBoundingClientRect()
  if (!rect) return
  tooltipPos.value = { top: rect.top - 8, left: rect.left + rect.width / 2 }
  tooltipEntries.value = entries
  tooltipVisible.value = true
}

function hideProposalsTooltip() {
  tooltipVisible.value = false
}

// Preferred team names
const preferredTeamNames = computed(() => {
  if (!props.preference.teams?.length || !props.teams?.length) return []
  return props.preference.teams
    .map(teamId => {
      const team = props.teams.find(t => t.simplicate_id === teamId)
      return team?.name || null
    })
    .filter(Boolean)
})

// Helper functions
const getEmployeeName = id => {
  return props.employeeMap[id] || id
}

const getEmployeeInfo = empId => {
  const emp = props.employeeDetails[empId]
  if (!emp) return ''
  const parts = []
  if (emp.function) parts.push(emp.function)
  if (emp.teams?.length > 0) parts.push(emp.teams.map(t => t.name).join(', '))
  return parts.join(' • ')
}

const getEmployeeWithWeeks = empId => {
  // First try preference-specific availability (fetched with preference dates)
  const prefAvailData = preferenceAvailability.value[empId]
  if (prefAvailData?.employee?.weeks?.length) {
    return prefAvailData.employee
  }
  // Fall back to parent-provided availability (fetched with project dates)
  const availData = props.employeeAvailability[empId]
  if (!availData?.employee) return null
  if (!availData.employee.weeks?.length) return null
  return availData.employee
}

const getMatchClass = matchPercent => {
  if (matchPercent >= 80)
    return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
  if (matchPercent >= 50)
    return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300'
  return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
}

const isEmployeeGraphExpanded = empId => {
  return props.expandedEmployeeGraphs[empId] !== false
}

// Selection handlers
const selectEmployee = empId => {
  emit('select', {
    employeeId: empId,
    dpw: props.preference.dpw,
    name: getEmployeeName(empId),
    isCustom: false
  })
}

const selectComparisonEmployee = compEmp => {
  emit('select-comparison', {
    employeeId: compEmp.employeeId,
    dpw: compEmp.dpw,
    name: compEmp.name,
    isCustom: true
  })
}
</script>
